/**
 * The single access-token holder for the tab.
 *
 * REST (`lib/api/client`) and the gateway handshake (`lib/realtime`) both read
 * it here, so the auth feature only ever has to call `setAccessToken` once. It
 * lives in memory on purpose — a token in `localStorage` is a token any XSS can
 * exfiltrate, and the refresh cookie is what survives a reload.
 */

type TokenListener = (token: string | null) => void;

let accessToken: string | null = null;
const listeners = new Set<TokenListener>();

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called by the auth feature on login, refresh and logout. */
export function setAccessToken(token: string | null): void {
  if (accessToken === token) return;
  accessToken = token;
  listeners.forEach((listener) => listener(token));
}

/** Lets the socket re-handshake the moment a refresh lands. */
export function onAccessTokenChange(listener: TokenListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/* ---------------------------------------------------------------------------
 * EXPIRY — for the one caller that cannot recover from presenting a dead token
 * ------------------------------------------------------------------------- */

/**
 * Treat a token as gone this long before `exp`.
 *
 * A handshake takes a round trip and the two clocks are not the same clock. A
 * token with four seconds left is a token that arrives expired.
 */
const EXPIRY_SKEW_MS = 30_000;

/**
 * `exp`, in local epoch ms, or `null` when the token does not carry one.
 *
 * READ, NOT VERIFIED — deliberately. The signature is the API's business and
 * this runs in the attacker's own browser, where verifying it would prove
 * nothing to anybody. All that is wanted is the number the server itself will
 * apply in a moment, so that the client can stop asking questions it already
 * knows the answer to.
 */
export function accessTokenExpiresAt(token: string): number | null {
  const payload = token.split('.')[1];
  if (payload === undefined || payload === '') return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const parsed: unknown = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
    if (typeof parsed !== 'object' || parsed === null) return null;
    const exp = (parsed as { exp?: unknown }).exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1_000 : null;
  } catch {
    /* Not a JWT, or not base64. Nothing is known about when it dies. */
    return null;
  }
}

/**
 * THE TOKEN, OR NULL IF IT IS ALREADY DEAD.
 *
 * `getAccessToken` cannot answer this question, and must not start to: REST
 * lives perfectly well with an expired token, because a 401 comes back, the
 * client refreshes and retries, and nobody notices (`lib/api/client.ts`).
 *
 * THE SOCKET HAS NO SUCH LUXURY, AND THAT IS THE 68-MINUTE BUG. The gateway
 * closes a connection at `exp` (apps/gateway/src/core/heartbeat.ts) with 4403,
 * the client reconnects — and `connect()` asked `getAccessToken()`, which
 * happily handed back the SAME dead string, so the handshake was refused,
 * closed 4403 again, and looped at the 30s backoff cap for ever. The branch
 * written to heal exactly this ran only when the token was `null`.
 *
 * Nothing broke the loop because an access token is only refreshed by an
 * OUTGOING HTTP REQUEST, and a tab sitting in a call makes none. On 21 Aug the
 * owner's token died 16 minutes before the other side hung up; `call.ended` was
 * written, published in 12ms, and delivered to a topic with no live subscriber.
 * He sat in a dead call until he pressed Hang up himself.
 *
 * So: an expired token is no token. The caller's existing no-token branch then
 * does the right thing — `refreshAccessToken()`, which is single-flight.
 */
export function getLiveAccessToken(): string | null {
  const token = accessToken;
  if (token === null) return null;
  const expiresAt = accessTokenExpiresAt(token);
  if (expiresAt === null) return token;
  return Date.now() >= expiresAt - EXPIRY_SKEW_MS ? null : token;
}
